import "./Overview.css";
import { useState, useEffect } from "react";
import { Selector } from "../../components/common/Selector";
import { MetricCard } from "../../components/common/MetricCard";
import { Chart } from "../../components/common/Chart";
import { fetchUniqueInteractions, fetchAgentHandoffs, fetchToolCalls, fetchErrors, fetchUniqueInteractionsOverTime, fetchToolCallsOverTime, fetchInsights, InsightData } from "../../api/metrics";
import { useGlobal } from "../../context/GlobalContext";

interface MetricData {
    value: number;
    change: number;
    sparkline: number[];
}

interface TimeSeriesData {
    timestamp: string;
    [key: string]: string | number;
}

interface MetricState {
    value: number;
    change: number;
    sparkline: number[];
}

export function Overview() {
    const { selectedProjectKey, userKey, isLoading: contextLoading } = useGlobal();
    const [selectedTime, setSelectedTime] = useState('24h');
    const [metrics, setMetrics] = useState<{
        uniqueInteractions: MetricState;
        agentHandoffs: MetricState;
        toolCalls: MetricState;
        errors: MetricState;
    }>({
        uniqueInteractions: { value: 0, change: 0, sparkline: [] },
        agentHandoffs: { value: 0, change: 0, sparkline: [] },
        toolCalls: { value: 0, change: 0, sparkline: [] },
        errors: { value: 0, change: 0, sparkline: [] }
    });
    const [interactionsOverTime, setInteractionsOverTime] = useState<any[]>([]);
    const [toolCallsOverTime, setToolCallsOverTime] = useState<any[]>([]);
    const [insights, setInsights] = useState<InsightData>({
        topAgent: { name: '', count: 0 },
        mostErrored: { tool: '', agent: '', count: 0 },
        commonError: ''
    });
    const [loadingCharts, setLoadingCharts] = useState(true);
    const [loadingMetrics, setLoadingMetrics] = useState(true);
    const [loadingInsights, setLoadingInsights] = useState(true);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        const loadMetrics = async () => {
            if (!selectedProjectKey || !userKey || contextLoading) return;
            
            setLoadingCharts(true);
            setLoadingMetrics(true);
            setLoadingInsights(true);
            setErrors({});
            
            const results = await Promise.allSettled([
                fetchUniqueInteractions(userKey, selectedProjectKey, selectedTime),
                fetchAgentHandoffs(userKey, selectedProjectKey, selectedTime),
                fetchToolCalls(userKey, selectedProjectKey, selectedTime),
                fetchErrors(userKey, selectedProjectKey, selectedTime),
                fetchUniqueInteractionsOverTime(userKey, selectedProjectKey, selectedTime),
                fetchToolCallsOverTime(userKey, selectedProjectKey, selectedTime),
                fetchInsights(userKey, selectedProjectKey, selectedTime)
            ]);
            
            const newErrors: Record<string, string> = {};
            
            if (results[0].status === 'fulfilled') {
                setMetrics(prev => ({ ...prev, uniqueInteractions: (results[0] as PromiseFulfilledResult<MetricData>).value }));
            } else {
                newErrors.uniqueInteractions = 'Failed to Retrieve Data';
            }
            
            if (results[1].status === 'fulfilled') {
                setMetrics(prev => ({ ...prev, agentHandoffs: (results[1] as PromiseFulfilledResult<MetricData>).value }));
            } else {
                newErrors.agentHandoffs = 'Failed to Retrieve Data';
            }
            
            if (results[2].status === 'fulfilled') {
                setMetrics(prev => ({ ...prev, toolCalls: (results[2] as PromiseFulfilledResult<MetricData>).value }));
            } else {
                newErrors.toolCalls = 'Failed to Retrieve Data';
            }
            
            if (results[3].status === 'fulfilled') {
                setMetrics(prev => ({ ...prev, errors: (results[3] as PromiseFulfilledResult<MetricData>).value }));
            } else {
                newErrors.errors = 'Failed to Retrieve Data';
            }
            
            if (results[4].status === 'fulfilled') {
                setInteractionsOverTime((results[4] as PromiseFulfilledResult<TimeSeriesData[]>).value);
            } else {
                newErrors.interactionsOverTime = 'Failed to Retrieve Data';
            }
            
            if (results[5].status === 'fulfilled') {
                setToolCallsOverTime((results[5] as PromiseFulfilledResult<TimeSeriesData[]>).value);
            } else {
                newErrors.toolCallsOverTime = 'Failed to Retrieve Data';
            }
            
            if (results[6].status === 'fulfilled') {
                setInsights((results[6] as PromiseFulfilledResult<InsightData>).value);
            } else {
                newErrors.insights = 'Failed to Retrieve Data';
            }
            
            setErrors(newErrors);
            setLoadingCharts(false);
            setLoadingMetrics(false);
            setLoadingInsights(false);
        };
        loadMetrics();
    }, [selectedTime, selectedProjectKey, userKey, contextLoading]);

    const timeOptions = [
        { key: '1h', label: '1 hour' },
        { key: '8h', label: '8 hours' },
        { key: '24h', label: '24 hours' },
        { key: '7d', label: '7 days' },
        { key: '15d', label: '15 days' },
        { key: '30d', label: '30 days' }
    ];

    return (
        <div className="overview-page">
            <div className="overview-header">
                <h1 className="overview-title">Overview</h1>
                <div className="time-selector">
                    <Selector
                        options={timeOptions}
                        selectedKey={selectedTime}
                        onSelect={setSelectedTime}
                    />
                </div>
            </div>

            <div className="metrics-grid">
                <MetricCard title="Unique Interactions" loading={loadingMetrics} error={errors.uniqueInteractions}>
                    <div className="metric-top">
                        <div className="metric-value">{metrics.uniqueInteractions.value.toLocaleString()}</div>
                        <div className={`metric-change ${metrics.uniqueInteractions.change >= 0 ? 'positive' : 'negative'}`}>
                            {metrics.uniqueInteractions.change >= 0 ? '↑' : '↓'} {Math.abs(metrics.uniqueInteractions.change)}%
                        </div>
                    </div>
                    <Chart data={metrics.uniqueInteractions.sparkline} color="#4ade80" height={80} showTooltip={false} />
                </MetricCard>

                <MetricCard title="Agent Handoffs" loading={loadingMetrics} error={errors.agentHandoffs}>
                    <div className="metric-top">
                        <div className="metric-value">{metrics.agentHandoffs.value.toLocaleString()}</div>
                        <div className={`metric-change ${metrics.agentHandoffs.change >= 0 ? 'positive' : 'negative'}`}>
                            {metrics.agentHandoffs.change >= 0 ? '↑' : '↓'} {Math.abs(metrics.agentHandoffs.change)}%
                        </div>
                    </div>
                    <Chart data={metrics.agentHandoffs.sparkline} color={metrics.agentHandoffs.change >= 0 ? '#4ade80' : '#f87171'} height={80} showTooltip={false} />
                </MetricCard>

                <MetricCard title="Tool Calls" loading={loadingMetrics} error={errors.toolCalls}>
                    <div className="metric-top">
                        <div className="metric-value">{metrics.toolCalls.value.toLocaleString()}</div>
                        <div className={`metric-change ${metrics.toolCalls.change >= 0 ? 'positive' : 'negative'}`}>
                            {metrics.toolCalls.change >= 0 ? '↑' : '↓'} {Math.abs(metrics.toolCalls.change)}%
                        </div>
                    </div>
                    <Chart data={metrics.toolCalls.sparkline} color="#4ade80" height={80} showTooltip={false} />
                </MetricCard>

                <MetricCard title="Errors" loading={loadingMetrics} error={errors.errors}>
                    <div className="metric-top">
                        <div className="metric-value">{metrics.errors.value.toLocaleString()}</div>
                        <div className={`metric-change ${metrics.errors.change >= 0 ? 'negative' : 'positive'}`}>
                            {metrics.errors.change >= 0 ? '↑' : '↓'} {Math.abs(metrics.errors.change)}%
                        </div>
                    </div>
                    <Chart data={metrics.errors.sparkline} color="#f87171" height={80} showTooltip={false} />
                </MetricCard>

                <MetricCard title="Unique Interactions Over Time" width="100%" height="470px" error={errors.interactionsOverTime}>
                    <div style={{ position: 'relative', height: '100%', overflow: 'hidden', paddingTop: '36px' }}>
                        <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '16px', zIndex: 10, background: 'rgba(20, 20, 20, 0.9)', padding: '6px 10px', borderRadius: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#0ea1eb' }}></div>
                                <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '13px', fontWeight: 500 }}>User</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#fb923c' }}></div>
                                <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '13px', fontWeight: 500 }}>Assistant</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <Chart 
                                    data={interactionsOverTime}
                                    showAxes={true}
                                    height={180}
                                    loading={loadingCharts}
                                    series={[
                                        { dataKey: 'user', name: 'User', color: '#33b2f2' }
                                    ]}
                                />
                            </div>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <Chart 
                                    data={interactionsOverTime}
                                    showAxes={true}
                                    height={180}
                                    loading={loadingCharts}
                                    series={[
                                        { dataKey: 'assistant', name: 'Assistant', color: '#fb923c' }
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                </MetricCard>

                <MetricCard title="Tool Calls Over Time" width="100%" height="470px" error={errors.toolCallsOverTime}>
                    <div style={{ position: 'relative', height: '100%', overflow: 'hidden', paddingTop: '36px' }}>
                        <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '16px', zIndex: 10, background: 'rgba(20, 20, 20, 0.9)', padding: '6px 10px', borderRadius: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#10b981' }}></div>
                                <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '13px', fontWeight: 500 }}>Tool Call</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ef4444' }}></div>
                                <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '13px', fontWeight: 500 }}>Error</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <Chart 
                                    data={toolCallsOverTime}
                                    showAxes={true}
                                    height={180}
                                    loading={loadingCharts}
                                    series={[
                                        { dataKey: 'tool_call', name: 'Tool Call', color: '#10b981' }
                                    ]}
                                />
                            </div>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <Chart 
                                    data={toolCallsOverTime}
                                    showAxes={true}
                                    height={180}
                                    loading={loadingCharts}
                                    series={[
                                        { dataKey: 'error', name: 'Error', color: '#ef4444' }
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                </MetricCard>
            </div>

            <div className="insights-section">
                <h2 className="insights-title">Insights</h2>
                <div className="insights-grid">
                    <MetricCard title="Top Performing Agent" accentColor="#e2d700" loading={loadingInsights} error={errors.insights}>
                        <div className="insight-name">{insights.topAgent.name}</div>
                        <div className="insight-detail">Interactions: <span>{insights.topAgent.count.toLocaleString()}</span></div>
                    </MetricCard>

                    <MetricCard title="Most Errored Tool/Agent" accentColor="#4ade80" loading={loadingInsights} error={errors.insights}>
                        <div className="insight-name">{insights.mostErrored.tool}</div>
                        <div className="insight-detail">Agent: <span>{insights.mostErrored.agent}</span></div>
                        <div className="insight-detail">Errors: <span>{insights.mostErrored.count}</span></div>
                    </MetricCard>

                    <MetricCard title="Most Common Error Message" accentColor="#f87171" loading={loadingInsights} error={errors.insights}>
                        <div className="insight-name-with-copy">
                            <span className="insight-name">
                                {insights.commonError.length > 40 ? insights.commonError.slice(0, 40) + "..." : insights.commonError}
                            </span>
                            <button 
                                className="copy-btn"
                                onClick={() => {
                                    navigator.clipboard.writeText(insights.commonError);
                                }}
                                title="Copy full message"
                            >
                                Copy
                            </button>
                        </div>
                    </MetricCard>
                </div>
            </div>
        </div>
    );
}
